import { useEffect, useRef, useState, useCallback, useId } from "react";
import { useParams } from "react-router-dom";
import { DocumentTextIcon } from "@heroicons/react/24/solid";
import type { Signing as SigningType } from "pandadoc-signing";

import Button from "components/Button";
import Modal from "components/Modal";
import { useDocumentSession } from "hooks/useArrangementAPI";

import ArrangementSpinner from "./ArrangementSpinner";
import ArrangementStepError from "./ArrangementStepError";
import type { DocumentSessionResult } from "./types";

type EmbedState = "minting" | "ready" | "error";

/**
 * Embeds the PandaDoc signing iframe for the arrangement document. The parent
 * (SignDocumentsStep) only renders this once the document is ready and the
 * viewer is allowed to sign; this component mints the embedded-signing session
 * and drives the SDK.
 *
 * The session is minted via `POST /arrangement/document/session` — the backend
 * resolves the correct recipient from the viewer and any recorded attestation.
 */
export default function PandaDocSigningSection({
  onCompleted,
}: {
  onCompleted: () => void;
}) {
  const { caseId } = useParams<{ caseId: string }>();
  const reactId = useId();
  const containerId = `pandadoc-signing-${reactId.replace(/:/g, "")}`;
  const { trigger: createSession } = useDocumentSession(caseId);

  const [embedState, setEmbedState] = useState<EmbedState>("minting");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(true);
  const [mintNonce, setMintNonce] = useState(0);
  const signingRef = useRef<SigningType | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoRecoveryCountRef = useRef(0);

  // Keep the latest onCompleted without re-running the embed effect when the
  // parent passes a fresh callback identity.
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  /** Safely tear down the PandaDoc SDK instance. */
  const destroySigning = useCallback(() => {
    if (signingRef.current) {
      try {
        signingRef.current.destroy();
      } catch {
        // PandaDoc may have already detached the DOM node
      }
      signingRef.current = null;
    }
    if (containerRef.current) {
      containerRef.current.innerHTML = "";
    }
  }, []);

  // Re-mint and reopen the embed. Shared by auto-recovery and the manual
  // "Try Again" button. Deliberately does NOT reset autoRecoveryCountRef:
  // auto-recovery increments the counter and then calls this, so resetting
  // here would wipe the increment and make the >= 2 budget unreachable
  // (looping forever). Only an explicit user retry restores the budget.
  const handleRetry = useCallback(() => {
    destroySigning();
    setSessionId(null);
    setEmbedState("minting");
    setModalOpen(true);
    setMintNonce((n) => n + 1);
  }, [destroySigning]);

  // Manual "Try Again": restore the auto-recovery budget, then re-mint.
  const handleManualRetry = useCallback(() => {
    autoRecoveryCountRef.current = 0;
    handleRetry();
  }, [handleRetry]);

  // Mint (or reuse) the signing session. The backend picks the right recipient
  // from the viewer + any recorded attestation.
  useEffect(() => {
    let cancelled = false;
    setEmbedState("minting");
    createSession({})
      .then((res) => {
        if (cancelled) return;
        const session = res as DocumentSessionResult | undefined;
        if (session?.session_id) {
          setSessionId(session.session_id);
          setEmbedState("ready");
        } else {
          setEmbedState("error");
        }
      })
      .catch(() => {
        if (!cancelled) setEmbedState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [createSession, mintNonce]);

  const isModalVisible = modalOpen && embedState === "ready";

  // Initialize the PandaDoc iframe once a session is ready and the modal opens.
  useEffect(() => {
    if (!isModalVisible || !sessionId || !containerRef.current) return;
    if (signingRef.current) return;

    let cancelled = false;

    import("pandadoc-signing").then(({ Signing }) => {
      if (cancelled) return;

      // SDK appends "px" to all values, making "100%" invalid — use placeholders
      const signing = new Signing(
        containerId,
        { sessionId, width: 1, height: 1 },
        { region: "com" },
      );

      signing.on("document.completed", () => {
        if (cancelled) return;
        setModalOpen(false);
        destroySigning();
        onCompletedRef.current();
      });

      signing.on("document.exception", () => {
        if (cancelled) return;
        // Auto-recover — re-minting fetches a fresh session, so if the doc was
        // regenerated under us we pick up the new one instead of stranding the
        // user on a voided document. Bounded so a persistent failure surfaces.
        if (autoRecoveryCountRef.current >= 2) {
          setEmbedState("error");
          return;
        }
        autoRecoveryCountRef.current += 1;
        handleRetry();
      });

      signing.open();

      requestAnimationFrame(() => {
        const iframe = document.getElementById(
          containerId,
        ) as HTMLIFrameElement;
        if (iframe) {
          iframe.style.width = "100%";
          iframe.style.height = `${Math.max(window.innerHeight - 160, 500)}px`;
        }
      });

      signingRef.current = signing;
    });

    return () => {
      cancelled = true;
      destroySigning();
    };
  }, [isModalVisible, sessionId, containerId, destroySigning, handleRetry]);

  // Auto-recover when the PandaDoc SDK throws an uncaught window error —
  // typically because the underlying document was voided under us (CS clicked
  // "Recreate Document" while this embed was open). Bounded so a persistently
  // failing SDK falls back to the manual error screen.
  useEffect(() => {
    if (embedState !== "ready") return;

    const isPandaDocError = (event: ErrorEvent) => {
      const msg = event.message || "";
      const src = event.filename || "";
      return (
        msg.includes("CombinedEventBus") ||
        msg.includes("pandadoc-signing") ||
        src.includes("pandadoc-signing")
      );
    };

    const handleWindowError = (event: ErrorEvent) => {
      if (!isPandaDocError(event)) return;
      if (autoRecoveryCountRef.current >= 2) return;
      autoRecoveryCountRef.current += 1;
      handleRetry();
    };

    window.addEventListener("error", handleWindowError);
    return () => window.removeEventListener("error", handleWindowError);
  }, [embedState, handleRetry]);

  const handleCloseModal = () => {
    destroySigning();
    setModalOpen(false);
  };

  const handleOpenModal = () => {
    // Mint a fresh session — the previous one may have expired while closed.
    // Set "minting" up front so the modal never renders open for a frame with
    // a null session (isModalVisible keys off embedState === "ready").
    setEmbedState("minting");
    setSessionId(null);
    setModalOpen(true);
    setMintNonce((n) => n + 1);
  };

  if (embedState === "error") {
    return (
      <ArrangementStepError message="We couldn't open your document for signing. Please try again.">
        <Button variant="primary" onClick={handleManualRetry}>
          Try Again
        </Button>
      </ArrangementStepError>
    );
  }

  if (embedState === "minting") {
    return (
      <div className="mx-auto max-w-2xl py-12 text-center">
        <ArrangementSpinner />
      </div>
    );
  }

  // Ready but the modal was closed — offer to reopen.
  if (!modalOpen) {
    return (
      <div className="mx-auto max-w-2xl py-12 text-center">
        <DocumentTextIcon className="mx-auto h-16 w-16 text-sage" />
        <h2 className="mt-4 font-serif text-2xl italic text-evergreen">
          Your document is ready
        </h2>
        <p className="mt-3 text-soil">
          Please review and sign the Statement of Funeral Goods and Services to
          continue.
        </p>
        <div className="mt-8 flex justify-center">
          <Button variant="primary" onClick={handleOpenModal}>
            Sign Document
          </Button>
        </div>
      </div>
    );
  }

  // Ready, modal open — render the embed.
  return (
    <>
      <div className="mx-auto max-w-2xl py-12 text-center">
        <ArrangementSpinner />
        <h2 className="mt-6 font-serif text-2xl italic text-evergreen">
          Opening document for signing…
        </h2>
      </div>
      <Modal
        isOpen={isModalVisible}
        onClose={handleCloseModal}
        fullScreen
        title="Review & Sign — Statement of Funeral Goods and Services"
      >
        <div id={containerId} ref={containerRef} className="w-full" />
      </Modal>
    </>
  );
}
